import { calculateGexByDte, type GexLevels, type OptionRow } from '../services/gexCalculator';
import type { Tier1GexMetrics, VolFlowMetrics, DteBucketRow } from '../types/marketAnalytics';

const GEX_HISTORY_KEY = 'mmec_gex_history_v1';
const GEX_HISTORY_MAX = 48;

function minutesToUsClose(): number {
  try {
    const now = new Date();
    const et = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const close = new Date(et);
    close.setHours(16, 0, 0, 0);
    return Math.max(0, Math.round((close.getTime() - et.getTime()) / 60000));
  } catch {
    return 0;
  }
}

function loadGexHistory(symbol: string): number[] {
  try {
    const raw = localStorage.getItem(GEX_HISTORY_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw) as Record<string, number[]>;
    return Array.isArray(all[symbol]) ? all[symbol] : [];
  } catch {
    return [];
  }
}

function pushGexHistory(symbol: string, dollarGex: number): number[] {
  const prev = loadGexHistory(symbol);
  const next = [...prev, dollarGex].slice(-GEX_HISTORY_MAX);
  try {
    const raw = localStorage.getItem(GEX_HISTORY_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, number[]>) : {};
    all[symbol] = next;
    localStorage.setItem(GEX_HISTORY_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
  return next;
}

function zscore(value: number, history: number[]): number | null {
  if (history.length < 3) return null;
  const mean = history.reduce((a, b) => a + b, 0) / history.length;
  const variance = history.reduce((s, v) => s + (v - mean) ** 2, 0) / history.length;
  const std = Math.sqrt(variance);
  if (std < 1e-6) return 0;
  return (value - mean) / std;
}

export function enrichGexMetricsFromClientHistory(
  symbol: string,
  gex: Tier1GexMetrics,
): Tier1GexMetrics {
  const history = pushGexHistory(symbol, gex.dollar_gex);
  const gex_zscore = zscore(gex.dollar_gex, history);
  const spot = gex.spot;
  const wallDist =
    spot > 0 && gex.call_wall != null && gex.put_wall != null
      ? Math.min(
          Math.abs(spot - gex.call_wall) / spot,
          Math.abs(spot - gex.put_wall) / spot,
        )
      : 1;
  const trap_zone = Boolean(
    gex_zscore != null && gex_zscore < -1 && wallDist < 0.03,
  );
  return { ...gex, gex_zscore, trap_zone };
}

function readFlowVolumes(row: OptionRow): {
  buyerCall: number;
  buyerPut: number;
  buyVol: number;
  sellVol: number;
  callPrem: number;
  putPrem: number;
} {
  const t = String(row['Option Type'] ?? row.optionType ?? '').toUpperCase();
  const isCall = t.startsWith('C');
  const side = String(row.Side ?? row.side ?? '').toLowerCase();
  const prem = Number(row.Premium ?? row.premium ?? 0) || 0;
  const vol = Number(row.Volume ?? row.volume ?? 0) || 0;
  const askVol =
    Number(
      row['Ask Volume accumelated'] ??
        row['Ask Volume'] ??
        row.askVolume ??
        0,
    ) || 0;
  const bidVol =
    Number(
      row['Bid Volume accumelated'] ??
        row['Bid Volume'] ??
        row.bidVolume ??
        0,
    ) || 0;
  const boughtCalls = Number(row.boughtCalls ?? 0) || 0;
  const soldCalls = Number(row.soldCalls ?? 0) || 0;
  const boughtPuts = Number(row.boughtPuts ?? 0) || 0;
  const soldPuts = Number(row.soldPuts ?? 0) || 0;

  let buyerCall = 0;
  let buyerPut = 0;
  let buyVol = 0;
  let sellVol = 0;
  let callPrem = isCall ? prem : 0;
  let putPrem = isCall ? 0 : prem;

  if (boughtCalls + soldCalls + boughtPuts + soldPuts > 0) {
    buyerCall += boughtCalls;
    buyerPut += boughtPuts;
    buyVol += boughtCalls + boughtPuts;
    sellVol += soldCalls + soldPuts;
  } else if (side.includes('buy') || side.includes('bought')) {
    buyVol += vol;
    if (isCall) buyerCall += prem > 0 ? prem : vol;
    else buyerPut += prem > 0 ? prem : vol;
  } else if (side.includes('sell') || side.includes('sold')) {
    sellVol += vol;
  } else if (askVol > 0 || bidVol > 0) {
    buyVol += askVol;
    sellVol += bidVol;
    if (isCall) buyerCall += askVol;
    else buyerPut += askVol;
    if (isCall) callPrem += askVol;
    else putPrem += askVol;
  }

  return { buyerCall, buyerPut, buyVol, sellVol, callPrem, putPrem };
}

export function buildGexMetrics(symbol: string, levels: GexLevels | null): Tier1GexMetrics {
  const spot = levels?.spot ?? 0;
  const net_gex = levels?.strikes?.reduce((s, k) => s + k.netGex, 0) ?? 0;
  const dollar_gex = net_gex;
  const history = pushGexHistory(symbol, dollar_gex);
  const gex_zscore = zscore(dollar_gex, history);

  const flip = levels?.gammaFlip ?? levels?.zeroGamma ?? null;
  const gamma_flip_distance =
    spot > 0 && flip != null ? Math.abs(spot - flip) / spot : null;

  const wallDist =
    spot > 0 && levels
      ? Math.min(
          Math.abs(spot - levels.callWall) / spot,
          Math.abs(spot - levels.putWall) / spot,
        )
      : 1;

  const trap_zone = Boolean(
    gex_zscore != null && gex_zscore < -1 && wallDist < 0.03,
  );

  const topOi = levels?.top5ByOI?.[0];
  const concentration =
    topOi && levels?.strikes?.length
      ? (topOi.callOI + topOi.putOI) /
        Math.max(
          1,
          levels.strikes.reduce((s, k) => s + k.callOI + k.putOI, 0),
        )
      : 0;
  const pin_probability =
    gamma_flip_distance != null
      ? Math.min(0.95, Math.max(0.05, concentration * (1 - Math.min(1, gamma_flip_distance * 8))))
      : null;

  const gamma_acceleration =
    spot > 0 && levels?.strikes?.length
      ? Math.abs(
          levels.strikes.reduce((s, k) => s + Math.abs(k.netGex), 0) /
            levels.strikes.length /
            spot,
        )
      : null;

  return {
    dollar_gex,
    gex_zscore,
    gamma_flip_distance,
    trap_zone,
    pin_probability,
    gamma_acceleration,
    minutes_to_close: minutesToUsClose(),
    net_gex,
    zero_gamma: levels?.zeroGamma ?? null,
    call_wall: levels?.callWall ?? null,
    put_wall: levels?.putWall ?? null,
    spot,
  };
}

export function buildVolFlowMetrics(rows: OptionRow[]): VolFlowMetrics {
  let callPrem = 0;
  let putPrem = 0;
  let buyerCall = 0;
  let buyerPut = 0;
  let ivSum = 0;
  let ivN = 0;
  let buyVol = 0;
  let sellVol = 0;
  let totalVol = 0;

  for (const row of rows) {
    const flow = readFlowVolumes(row);
    buyerCall += flow.buyerCall;
    buyerPut += flow.buyerPut;
    buyVol += flow.buyVol;
    sellVol += flow.sellVol;
    callPrem += flow.callPrem;
    putPrem += flow.putPrem;
    totalVol += Number(row.Volume ?? row.volume ?? 0) || 0;
    const iv = Number(row['Implied Volatility'] ?? row.iv ?? 0);
    if (iv > 0) {
      ivSum += iv;
      ivN += 1;
    }
  }

  const totalBuyer = buyerCall + buyerPut;
  const signed_buyer_pcr =
    totalBuyer > 0
      ? buyerPut / Math.max(buyerCall, 1)
      : putPrem > 0
        ? putPrem / Math.max(callPrem, 1)
        : null;

  const ivMean = ivN > 0 ? ivSum / ivN : null;
  const z_vov = ivMean != null ? Math.abs(ivMean - 0.2) / Math.max(ivMean, 0.05) : null;
  const iv_har_gap = ivMean != null ? ivMean - 0.18 : null;

  const hasFlow = buyVol + sellVol > 0;
  const imbalance = Math.abs(buyVol - sellVol);
  const vpin_rolling = !hasFlow
    ? null
    : totalVol > 0
      ? imbalance / totalVol
      : imbalance / (buyVol + sellVol);
  const rvolProxy = totalVol / Math.max(rows.length, 1) / 500;
  const flow_toxicity = Boolean(vpin_rolling != null && vpin_rolling > 0.6 && rvolProxy > 2.5);

  return { z_vov, iv_har_gap, signed_buyer_pcr, vpin_rolling, flow_toxicity };
}

function parseDte(row: OptionRow): number | null {
  const raw = row.DTE ?? row.dte ?? row['Days To Expiration'] ?? row.daysToExpiration;
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0) return n;
  const exp = String(row.Expiration ?? row.expiration ?? row['Expiration Date'] ?? '');
  if (!exp) return null;
  const d = new Date(exp);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / 86400000));
}

function dteBucketLabel(dte: number): DteBucketRow['bucket'] {
  if (dte <= 4) return '0-4d';
  if (dte <= 21) return '7-21d';
  if (dte <= 45) return '30-45d';
  return '45d+';
}

export function buildDteBuckets(rows: OptionRow[]): DteBucketRow[] {
  const acc: Record<DteBucketRow['bucket'], DteBucketRow> = {
    '0-4d': { bucket: '0-4d', net_gex: 0, open_interest: 0, volume: 0, contract_count: 0 },
    '7-21d': { bucket: '7-21d', net_gex: 0, open_interest: 0, volume: 0, contract_count: 0 },
    '30-45d': { bucket: '30-45d', net_gex: 0, open_interest: 0, volume: 0, contract_count: 0 },
    '45d+': { bucket: '45d+', net_gex: 0, open_interest: 0, volume: 0, contract_count: 0 },
  };

  for (const row of rows) {
    const dte = parseDte(row);
    if (dte == null) continue;
    const bucket = dteBucketLabel(dte);
    const oi = Number(row['Open Interest'] ?? row.open_interest ?? 0) || 0;
    const vol = Number(row.Volume ?? row.volume ?? 0) || 0;
    const gamma = Number(row.Gamma ?? row.gamma ?? 0.01) || 0.01;
    const spot = Number(row['Underlying Price'] ?? row.underlying_price ?? 0) || 100;
    const sign = String(row['Option Type'] ?? row.optionType ?? '').toUpperCase().startsWith('C') ? 1 : -1;
    const gex = gamma * oi * 100 * spot * sign * 0.01;
    acc[bucket].net_gex += gex;
    acc[bucket].open_interest += oi;
    acc[bucket].volume += vol;
    acc[bucket].contract_count += 1;
  }

  return (['0-4d', '7-21d', '30-45d', '45d+'] as const).map((b) => acc[b]);
}

export function gexLevelsFromRows(rows: OptionRow[]): GexLevels | null {
  const groups = calculateGexByDte(rows);
  if (!groups.length) return null;
  return groups.reduce((best, g) =>
    g.levels.strikes.length > (best?.strikes.length ?? 0) ? g.levels : best,
  groups[0].levels);
}

export function readGexHistory(symbol: string): number[] {
  return loadGexHistory(symbol);
}
